/**
 * OpenStreetMap's power lines, fences, walls, hedges and masts, from Overpass.
 *
 * The streamed Shortbread tiles have none of them. Overpass serves them with
 * their geometry (`out geom`): a line's nodes, which for a power line are its
 * pylons; a mast's point. `loaders/osmLineService.ts` fetches them a block at
 * a time, as the building tags are.
 *
 * Pure: no fetch, no DOM.
 */

import type { TileBounds } from '../geo/tilekey.js';

export type OsmLineKind = 'power' | 'minor_power' | 'fence' | 'wall' | 'hedge';
export type OsmPointKind = 'mast';

export interface OsmLine {
  kind: OsmLineKind;
  /** lon, lat pairs, in the way's order. */
  points: number[];
  /** `height`, metres, when tagged. */
  height?: number;
  /** On a bridge (`bridge`, or `layer` 1 or more): stands on the deck, not the ground under it. */
  raised?: boolean;
  /** Metal (`material=metal`/`steel`, or a `fence_type` that is): a railing, not a wooden fence. */
  metal?: boolean;
}

/** Whether a barrier's tags put it up on a bridge. */
export function raisedBy(bridge: string | undefined, layer: string | undefined): boolean {
  return (bridge !== undefined && bridge !== '' && bridge !== 'no') || Number(layer) >= 1;
}

/** Whether a fence's tags make it metal. */
export function metalBy(material: string | undefined, fenceType: string | undefined): boolean {
  return /^(metal|steel|iron|aluminium)$/.test(material ?? '') || /^(railing|metal|chain_link|bars|guard_rail|wire)$/.test(fenceType ?? '');
}

export interface OsmPoint {
  kind: OsmPointKind;
  lon: number;
  lat: number;
  height?: number;
}

export type OsmFeature = OsmLine | OsmPoint;

export const isLine = (f: OsmFeature): f is OsmLine => 'points' in f;

/**
 * The Overpass QL for a box: power lines (`line`, `minor_line`), barriers
 * (fences, walls and hedges), and masts: `man_made=mast`, and towers that are
 * for telecommunication (`tower:type=communication`), the cell towers.
 */
export function overpassLineQuery(bounds: TileBounds, timeoutSeconds = 25): string {
  const box = [bounds.south, bounds.west, bounds.north, bounds.east].map(v => v.toFixed(6)).join(',');
  return `[out:json][timeout:${timeoutSeconds}];(` +
    `way["power"~"^(line|minor_line)$"](${box});` +
    `way["barrier"~"^(fence|wall|retaining_wall|city_wall|hedge)$"](${box});` +
    `node["man_made"="mast"](${box});` +
    `nwr["man_made"="tower"]["tower:type"="communication"](${box});` +
    `);out geom qt;`;
}

export function overpassLineUrl(endpoint: string, bounds: TileBounds): string {
  return `${endpoint}?data=${encodeURIComponent(overpassLineQuery(bounds))}`;
}

/** A `height` tag in metres ("12", "12 m"), or undefined for anything else or out of reason. */
export function heightOf(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const m = /^\s*([0-9]+(?:\.[0-9]+)?)\s*(m)?\s*$/.exec(raw);
  if (!m) return undefined;
  const h = Number(m[1]);
  return h > 0 && h < 400 ? h : undefined;
}

interface OverpassElement {
  type: 'node' | 'way' | 'relation';
  lat?: number;
  lon?: number;
  geometry?: Array<{ lat: number; lon: number } | null>;
  bounds?: { minlat: number; minlon: number; maxlat: number; maxlon: number };
  tags?: Record<string, string>;
}

/** Parse the JSON Overpass returns for `overpassLineQuery`. */
export function parseOverpassLines(text: string): OsmFeature[] {
  let elements: OverpassElement[];
  try {
    elements = (JSON.parse(text) as { elements?: OverpassElement[] }).elements ?? [];
  } catch {
    return [];
  }
  const out: OsmFeature[] = [];
  for (const e of elements) {
    const t = e.tags ?? {};
    const height = heightOf(t.height);
    if (t.man_made === 'mast' || (t.man_made === 'tower' && t['tower:type'] === 'communication')) {
      // A tower mapped as an outline stands at its middle.
      let lat = e.lat, lon = e.lon;
      if ((lat === undefined || lon === undefined) && e.bounds) {
        lat = (e.bounds.minlat + e.bounds.maxlat) / 2;
        lon = (e.bounds.minlon + e.bounds.maxlon) / 2;
      }
      if (lat !== undefined && lon !== undefined) out.push({ kind: 'mast', lon, lat, ...(height ? { height } : {}) });
      continue;
    }
    if (e.type !== 'way' || !e.geometry || e.geometry.length < 2) continue;
    const kind: OsmLineKind | undefined = t.power === 'line' ? 'power' : t.power === 'minor_line' ? 'minor_power'
      : t.barrier === 'fence' ? 'fence' : t.barrier === 'hedge' ? 'hedge'
        : t.barrier === 'wall' || t.barrier === 'retaining_wall' || t.barrier === 'city_wall' ? 'wall' : undefined;
    if (kind === undefined) continue;
    const points: number[] = [];
    for (const g of e.geometry) if (g) points.push(g.lon, g.lat);
    if (points.length < 4) continue;
    out.push({
      kind, points, ...(height ? { height } : {}),
      ...(raisedBy(t.bridge, t.layer) ? { raised: true } : {}),
      ...(metalBy(t.material, t.fence_type) ? { metal: true } : {}),
    });
  }
  return out;
}

/** The features touching `bounds` (a line by its box, a little past the edge; a point inside it). */
export function featuresWithin(features: readonly OsmFeature[], bounds: TileBounds, margin = 0.0005): OsmFeature[] {
  return features.filter((f) => {
    if (!isLine(f)) return f.lon >= bounds.west && f.lon < bounds.east && f.lat > bounds.south && f.lat <= bounds.north;
    let w = Infinity, e = -Infinity, s = Infinity, n = -Infinity;
    for (let i = 0; i < f.points.length; i += 2) {
      w = Math.min(w, f.points[i]); e = Math.max(e, f.points[i]);
      s = Math.min(s, f.points[i + 1]); n = Math.max(n, f.points[i + 1]);
    }
    return e >= bounds.west - margin && w <= bounds.east + margin && n >= bounds.south - margin && s <= bounds.north + margin;
  });
}
